import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { TbCheck } from 'react-icons/tb';

interface LastSavedTimestampProps {
	lastSavedAt: Date | null;
	isLoading: boolean;
}

const LastSavedTimestamp: React.FC<LastSavedTimestampProps> = ({ lastSavedAt, isLoading }) => {
	const [relativeTime, setRelativeTime] = useState<string>('');

	// Function to calculate relative time from UTC timestamp
	const getRelativeTime = (utcDate: Date): string => {
		const now = new Date();
		const diffInSeconds = Math.floor((now.getTime() - utcDate.getTime()) / 1000);

		if (diffInSeconds < 10) {
			return 'just now';
		} else if (diffInSeconds < 60) {
			return `${diffInSeconds} seconds ago`;
		} else if (diffInSeconds < 3600) {
			const minutes = Math.floor(diffInSeconds / 60);
			return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
		} else if (diffInSeconds < 86400) {
			const hours = Math.floor(diffInSeconds / 3600);
			return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
		} else {
			const days = Math.floor(diffInSeconds / 86400);
			return `${days} ${days === 1 ? 'day' : 'days'} ago`;
		}
	};

	// Update relative time display
	useEffect(() => {
		if (!lastSavedAt) {
			setRelativeTime('');
			return;
		}

		// Initial update
		setRelativeTime(getRelativeTime(lastSavedAt));

		// Update every 10 seconds
		const interval = setInterval(() => {
			setRelativeTime(getRelativeTime(lastSavedAt));
		}, 10000);

		return () => clearInterval(interval);
	}, [lastSavedAt]);

	// Show loading state
	if (isLoading) {
		return (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1,
					color: 'text.secondary',
				}}
			>
				<CircularProgress size={16} />
				<Typography variant="body2" color="text.secondary">
					Saving...
				</Typography>
			</Box>
		);
	}

	// Show last saved timestamp
	if (lastSavedAt && relativeTime) {
		return (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1,
					color: 'success.main',
				}}
			>
				<TbCheck size={16} />
				<Typography variant="body2" color="text.secondary">
					Last saved {relativeTime}
				</Typography>
			</Box>
		);
	}

	// Don't show anything if no timestamp
	return null;
};

export { LastSavedTimestamp };
